import { TypeRegistry } from '@fastify/type-provider-typebox';
import { Kind, type Static, type TSchema, Type } from '@sinclair/typebox';
import { REGEX } from '../schemas/common.js';
import { isValidObjectId } from '../utils/index.js';

export const HexColorSchema = Type.String({ pattern: REGEX.HEX_COLOR.source });
export const MongoIdStringSchema = Type.String({ pattern: REGEX.MONGO_ID.source });
export const UrlSchema = Type.String({ pattern: REGEX.URL.source });
export const UUIDSchema = Type.String({ pattern: REGEX.UUID.source });
export const PasswordSchema = Type.String({ minLength: 8, maxLength: 64 });

TypeRegistry.Set('MongoObjectId', (schema: any, value: any) => isValidObjectId(value));

export const MongooseObjectIdOrStringSchema = Type.Transform(
  Type.Union([MongoIdStringSchema, Type.Unsafe<any>({ [Kind]: 'MongoObjectId' })]),
)
  .Decode(v => v)
  .Encode(v => (typeof v === 'string' ? v : v._id.toString()));

export function MongooseRefSchema<T extends TSchema>(item: T) {
  return Type.Union([item, MongooseObjectIdOrStringSchema]);
}

export function TransformedRefToString<
  T extends ReturnType<typeof MongooseRefSchema> = ReturnType<typeof MongooseRefSchema>,
>(schema: T) {
  return Type.Transform(schema)
    .Decode(v => v)
    .Encode(v => (!!v && typeof v === 'object' && '_id' in v ? (v._id as any).toString() : v));
}

export function PaginateDTOSchema<T extends TSchema>(item: T) {
  return Type.Object({
    docs: Type.Array(item),
    totalDocs: Type.Number(),
    limit: Type.Number(),
    hasPrevPage: Type.Boolean(),
    hasNextPage: Type.Boolean(),
    page: Type.Optional(Type.Number()),
    totalPages: Type.Number(),
    offset: Type.Optional(Type.Number()),
    prevPage: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
    nextPage: Type.Optional(Type.Union([Type.Optional(Type.Number()), Type.Null()])),
    pagingCounter: Type.Number(),
    meta: Type.Optional(Type.Any()),
  });
}

export type PaginateDTO<T extends TSchema> = Static<ReturnType<typeof PaginateDTOSchema<T>>>;

export const NullableDTO = <T extends TSchema>(item: T) => Type.Union([item, Type.Null()]);
export const NullOrUndefinedDTO = <T extends TSchema>(item: T) => Type.Union([item, Type.Undefined(), Type.Null()]);
